import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { zodResolver } from '@hookform/resolvers/zod';

import { LoadingButton } from '@mui/lab';
import { Button, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import { DEFAULT_ERROR_MESSAGE } from '@/constants/app/config';
import { useUpdateMachineMutation } from '@/store/api/services/machines/machine';
import type { MachineDetail } from '@/types';

const EditMachineLocksModemSchema = z.object({
  lock_set_to: z.string().optional(),
  padlock: z.string().optional(),
  lock_serial_number: z.string().optional(),
  modem_serial: z.string().optional(),
});

type EditMachineLocksModemSchemaType = z.infer<typeof EditMachineLocksModemSchema>;

/**
 * Edit Machine Locks/Modem Modal
 * Boro's PUT /v2/atms/{atmId} overwrites name/pick_up_id unconditionally on every save (no
 * partial update on that endpoint), so this always resubmits the machine's current name and
 * pick_up_id alongside the lock/modem fields being edited here, to avoid blanking them out.
 * Serial is intentionally excluded -- it's edited through its own dedicated confirmation flow.
 */
const EditMachineLocksModem = ({ hide, props }: ModalChildrenProps) => {
  const data = props?.data as MachineDetail | undefined;

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<EditMachineLocksModemSchemaType>({
    resolver: zodResolver(EditMachineLocksModemSchema),
    mode: 'onSubmit',
    defaultValues: {
      lock_set_to: data?.lock_set_to ?? '',
      padlock: data?.padlock ?? '',
      lock_serial_number: data?.lock_serial_number ?? '',
      modem_serial: data?.modem_serial ?? '',
    },
  });

  const [updateMachine] = useUpdateMachineMutation();

  const onSubmit = async (payload: EditMachineLocksModemSchemaType) => {
    try {
      await updateMachine({
        atm_id: data!.id,
        name: data!.name,
        pick_up_id: data!.pick_up_id,
        lock_set_to: payload.lock_set_to,
        padlock: payload.padlock,
        lock_serial_number: payload.lock_serial_number,
        modem_serial: payload.modem_serial,
      }).unwrap();

      toast.success('Lock/modem details updated successfully');
      hide();
    } catch (error) {
      toast.error((error as any)?.message ?? DEFAULT_ERROR_MESSAGE);
    }
  };

  return (
    <>
      <DialogContent sx={{ py: 4 }}>
        <Stack gap={2} data-test="edit-machine-locks-modem-form">
          <TextField label="Lock Set To" size="small" fullWidth {...register('lock_set_to')} />
          <TextField label="Padlock" size="small" fullWidth {...register('padlock')} />
          <TextField
            label="Lock Serial Number"
            size="small"
            fullWidth
            {...register('lock_serial_number')}
          />
          <TextField label="Modem Serial" size="small" fullWidth {...register('modem_serial')} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={() => hide()} disabled={isSubmitting}>
          Discard changes
        </Button>
        <LoadingButton
          variant="contained"
          disabled={isSubmitting}
          loading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        >
          Save Changes
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default EditMachineLocksModem;
